import isObject from "lodash/isObject";
import { useRef, useLayoutEffect, useCallback } from "react";

/**
 * A schema for validating an object
 */
export type ValidationSchema<T extends Record<string, any>> = {
  [key in keyof Partial<T>]: (
    t: T
  ) => string | undefined | { [key: string]: any };
};

/** @private is the given object a Function? */
export const isFunction = (obj: any): obj is (...args: any) => any =>
  typeof obj === "function";

/**
 * @param values The object to be validated
 * @param schema A ValidationSchema
 * @returns An object with the keys being the field names and the values being the error messages
 */
export function validate<T extends Record<string, any>>(
  values: T,
  schema: ValidationSchema<T>
) {
  const errors = {} as any;

  Object.entries(schema).forEach(([key, validator]) => {
    const result = validator(values);

    if (result) {
      if (isObject(result) && Object.keys(result).length === 0) return;
      errors[key] = result;
    }
  });

  return errors;
}

export const useEventCallback = <T extends (...args: any[]) => any>(
  fn: T
): T => {
  const ref: any = useRef(fn);

  // we copy a ref to the callback scoped to the current state/props on each render
  useLayoutEffect(() => {
    ref.current = fn;
  });

  return useCallback(
    (...args: any[]) => ref.current.apply(void 0, args),
    []
  ) as T;
};

/**
 * Recursively a set the same value for all keys and arrays nested object, cloning
 * @param object
 * @param value
 * @param visited
 * @param response
 */
export const setNestedObjectValues = <T>(
  object: any,
  value: any,
  visited: any = new WeakMap(),
  response: any = {}
): T => {
  for (const k of Object.keys(object)) {
    const val = object[k];
    if (isObject(val)) {
      if (!visited.get(val)) {
        visited.set(val, true);
        // In order to keep array values consistent for both dot path  and
        // bracket syntax, we need to check if this is an array so that
        // this will output  { friends: [true] } and not { friends: { "0": true } }
        response[k] = Array.isArray(val) ? [] : {};
        setNestedObjectValues(val, value, visited, response[k]);
      }
    } else {
      response[k] = value;
    }
  }

  return response;
};
